import type {
  AirConnectionResponse,
  AirConnectorName,
  AirErrorCode,
  AirProviderCapabilities,
} from '@bandido/shared/types';

/** Estado de la conexión para el chip de la pantalla. */
export type AirConnectionStatus =
  | 'notConfigured'
  | 'unchecked'
  | 'ok'
  | 'credentialIssues'
  | 'keyExpiring'
  | 'keyRejected'
  | 'unavailable';

export function connectionStatus(connection: AirConnectionResponse | undefined): AirConnectionStatus {
  if (!connection?.configured) return 'notConfigured';
  const { health } = connection;
  if (!health) return 'unchecked';
  if (health.status !== 'ok') return health.status === 'key_rejected' ? 'keyRejected' : 'unavailable';
  if (health.apiKey?.expiringSoon) return 'keyExpiring';
  if (health.summary.connectorsWithIssues > 0) return 'credentialIssues';
  return 'ok';
}

export const STATUS_COLOR: Record<AirConnectionStatus, 'default' | 'success' | 'warning' | 'error'> = {
  notConfigured: 'default',
  unchecked: 'default',
  ok: 'success',
  credentialIssues: 'warning',
  keyExpiring: 'warning',
  keyRejected: 'error',
  unavailable: 'warning',
};

/** `TEST` y `PRODUCTION` tienen nombre; otro valor de PowerFlights se muestra tal cual. */
export function environmentKey(environment: string | null): string | null {
  if (environment === 'TEST') return 'airConnections.environment.test';
  if (environment === 'PRODUCTION') return 'airConnections.environment.production';
  return null;
}

export type KeyExpiry = { kind: 'never' } | { kind: 'expired' } | { kind: 'days'; days: number } | { kind: 'unknown' };

export function keyExpiry(connection: AirConnectionResponse | undefined): KeyExpiry {
  if (!connection?.configured || connection.health?.status !== 'ok' || !connection.health.apiKey) return { kind: 'unknown' };
  const days = connection.health.apiKey.expiresInDays;
  if (days === null) return { kind: 'never' };
  if (days < 0) return { kind: 'expired' };
  return { kind: 'days', days };
}

export const PROVIDER_NAMES: Record<AirConnectorName, string> = {
  AMADEUS_EDIFACT: 'Amadeus EDIFACT',
  AMADEUS_NDC: 'Amadeus NDC',
  DUFFEL: 'Duffel',
  STARLINGS: 'Starlings',
  MYSTIFLY: 'Mystifly',
  SABRE_MOSAIC: 'Sabre',
};

/** Las capacidades que se muestran como chips, en el orden del recorrido de venta. */
export const CAPABILITY_CHIPS: Array<keyof Omit<AirProviderCapabilities, 'issuesAtCreate'>> = [
  'search',
  'create',
  'issue',
  'void',
  'cancel',
  'refund',
];

/** Prender un permiso pide confirmación; apagarlo no. */
export function needsConfirmation(current: boolean, next: boolean): boolean {
  return !current && next;
}

/**
 * Error de un pedido de vuelos, sin nada del pedido: el error de axios lleva
 * el cuerpo (la API key, una credencial) y no se guarda en el estado de
 * react-query ni se muestra.
 */
export class AirRequestError extends Error {
  constructor(
    readonly messageKey: string,
    readonly fields: string[] = [],
    /** Un cambio en PowerFlights que pudo haberse aplicado: no se repite a ciegas. */
    readonly uncertain = false,
  ) {
    super(messageKey);
    this.name = 'AirRequestError';
  }
}

const ERROR_KEYS: Partial<Record<AirErrorCode, string>> = {
  AIR_CONNECTION_KEY_REJECTED: 'airConnections.errors.keyRejected',
  AIR_HUB_KEY_REJECTED: 'airConnections.errors.hubKeyRejected',
  AIR_CONNECTION_KEY_REQUIRED: 'airConnections.errors.keyRequired',
  AIR_CONNECTION_CHANGED: 'airConnections.errors.changed',
  AIR_CONNECTION_NOT_FOUND: 'airConnections.errors.notConnected',
  AIR_CONNECTION_NOT_SHARED: 'airConnections.errors.notShared',
  AIR_HUB_NOT_CONFIGURED: 'airConnections.errors.notConfigured',
  AIR_CONNECTION_STORAGE_NOT_CONFIGURED: 'airConnections.errors.notConfigured',
  AIR_CONNECTION_KEY_UNREADABLE: 'airConnections.errors.keyUnreadable',
  AIR_HUB_UNAVAILABLE: 'airConnections.errors.unavailable',
  AIR_HUB_INVALID_RESPONSE: 'airConnections.errors.unavailable',
  AIR_HUB_FORBIDDEN: 'airConnections.errors.forbidden',
  AIR_HUB_NOT_FOUND: 'airConnections.errors.notFound',
  AIR_HUB_CONFLICT: 'airConnections.errors.conflict',
  AIR_PROVIDER_UNKNOWN: 'airConnections.errors.providerUnknown',
  AIR_CREDENTIAL_INVALID: 'airConnections.errors.fields',
  AIR_HUB_REQUEST_REJECTED: 'airConnections.errors.rejected',
  AIR_REQUEST_INVALID: 'airConnections.errors.rejected',
  AIR_SEARCH_INVALID: 'airSearch.errors.invalid',
  AIR_SEARCH_LIMIT: 'airSearch.errors.limit',
  AIR_SEARCH_NOT_FOUND: 'airSearch.errors.notFound',
  AIR_OFFER_NOT_FOUND: 'airOfferDetail.errors.notFound',
  AIR_OFFER_EXPIRED: 'airOfferDetail.errors.expired',
  AIR_BOOKING_NOT_FOUND: 'airBooking.errors.notFound',
  AIR_BOOKING_ATTEMPT_CHANGED: 'airBooking.errors.attemptChanged',
  AIR_BOOKING_NOT_READY: 'airBooking.errors.notReady',
  AIR_BOOKING_QUOTE_CHANGED: 'airBooking.errors.quoteChanged',
  AIR_BOOKINGS_DISABLED: 'airBooking.errors.disabled',
  AIR_BOOKING_CASE_NOT_FOUND: 'airBooking.errors.caseNotFound',
  AIR_BOOKING_CASE_WITHOUT_CLIENT: 'airBooking.errors.caseWithoutClient',
  AIR_BOOKING_TRAVELER_INVALID: 'airBooking.errors.travelers',
  AIR_BOOKING_PASSENGER_AGE_MISMATCH: 'airBooking.errors.passengerAge',
  AIR_BOOKING_BUYER_INCOMPLETE: 'airBooking.errors.buyerIncomplete',
  AIR_BOOKING_BRAND_UNCONFIRMED: 'airBooking.errors.brandUnconfirmed',
  AIR_BOOKING_ISSUES_AT_CREATE: 'airBooking.errors.issuesAtCreate',
  AIR_BOOKING_EXTRAS_UNSUPPORTED: 'airBooking.errors.extrasUnsupported',
  AIR_BOOKING_EXTRAS_INVALID: 'airBooking.errors.extrasInvalid',
  AIR_BOOKING_OFFER_ALREADY_SENT: 'airBooking.errors.offerAlreadySent',
  AIR_BOOKING_NOT_SYNCABLE: 'airBooking.errors.notSyncable',
  AIR_ISSUE_DISABLED: 'airBooking.issue.errors.disabled',
  AIR_ISSUE_NOT_AVAILABLE: 'airBooking.issue.errors.notAvailable',
  AIR_ISSUE_TOTAL_CHANGED: 'airBooking.issue.errors.totalChanged',
  AIR_ISSUE_ALREADY_SENT: 'airBooking.issue.errors.alreadySent',
  AIR_ISSUE_APPROVAL_REQUIRED: 'airBooking.issue.errors.approvalRequired',
  AIR_POST_SALE_NOT_AVAILABLE: 'airBooking.postSale.errors.notAvailable',
  AIR_POST_SALE_ALREADY_SENT: 'airBooking.postSale.errors.alreadySent',
  AIR_AIRLINE_CHANGE_NOT_AVAILABLE: 'airBooking.airlineChanges.errors.notAvailable',
  AIR_AIRLINE_CHANGE_ALREADY_SENT: 'airBooking.airlineChanges.errors.alreadySent',
};

interface ErrorFacts {
  status?: number;
  code?: AirErrorCode;
  hubCode?: unknown;
  fields: string[];
  writeNotSent: boolean;
  /** El motivo de un 409 de post-venta (`AIR_POST_SALE_NOT_AVAILABLE`). */
  reason?: string;
}

/** Lo que importa de la respuesta de error de client-api (`message` del filtro global). */
function errorFacts(error: unknown): ErrorFacts {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })?.response;
  const data = response?.data?.message;
  const body = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : {};
  return {
    status: typeof response?.status === 'number' ? response.status : undefined,
    code: typeof body.code === 'string' ? (body.code as AirErrorCode) : undefined,
    hubCode: body.hubCode,
    fields: Array.isArray(body.fields) ? body.fields.filter((f): f is string => typeof f === 'string') : [],
    writeNotSent: body.writeNotSent === true,
    reason: typeof body.reason === 'string' ? body.reason : undefined,
  };
}

/** Los motivos con aviso propio al decidir un cambio de la aerolínea (docs/AIR.md, "Cambios de la aerolínea"). */
const AIRLINE_CHANGE_REASONS = new Set([
  'CHANGE_NOT_FOUND',
  'CHANGE_RESOLVED',
  'DECISION_NOT_AVAILABLE',
  'NOT_SUPPORTED',
  'POST_SALE_IN_PROGRESS',
  'ISSUE_IN_PROGRESS',
  'BOOKING_CLOSED',
  'DECISION_CHANGED',
]);

/** Los motivos de post-venta con un aviso propio (docs/AIR.md, "Post-venta"). */
const POST_SALE_REASONS = new Set([
  'NO_LIVE_TICKETS',
  'NOT_REFUNDABLE',
  'REFUND_NOT_SUPPORTED',
  'ISSUE_IN_PROGRESS',
  'BOOKING_CLOSED',
  'ALREADY_CANCELLED',
  'ALREADY_REFUNDED',
  'AIRLINE_CHANGE_IN_PROGRESS',
]);

function messageFor(facts: ErrorFacts): { key: string; fields: string[] } {
  if (facts.code === 'AIR_HUB_FORBIDDEN' && facts.hubCode === 'SCOPE_REQUIRED') return { key: 'airConnections.errors.scopeRequired', fields: [] };
  if (facts.code === 'AIR_HUB_REQUEST_REJECTED' && facts.fields.length) return { key: 'airConnections.errors.fields', fields: facts.fields };
  if (facts.code === 'AIR_POST_SALE_NOT_AVAILABLE' && facts.reason && POST_SALE_REASONS.has(facts.reason)) return { key: `airBooking.postSale.reason.${facts.reason}`, fields: [] };
  if (facts.code === 'AIR_AIRLINE_CHANGE_NOT_AVAILABLE' && facts.reason && AIRLINE_CHANGE_REASONS.has(facts.reason)) return { key: `airBooking.airlineChanges.reason.${facts.reason}`, fields: [] };
  const known = facts.code && ERROR_KEYS[facts.code];
  if (known) return { key: known, fields: facts.fields };
  // Un 4xx sin código propio (el ValidationPipe, la autoridad de la agencia) es
  // un rechazo de client-api: nada llegó a PowerFlights.
  if (facts.status === 403) return { key: 'airConnections.errors.noAccess', fields: [] };
  if (facts.status && facts.status >= 400 && facts.status < 500) return { key: 'airConnections.errors.rejected', fields: [] };
  return { key: 'airConnections.errors.generic', fields: [] };
}

/** Traduce la respuesta de error de client-api a una clave de i18n y los campos que nombra. */
export function toAirRequestError(error: unknown): AirRequestError {
  const { key, fields } = messageFor(errorFacts(error));
  return new AirRequestError(key, fields);
}

/**
 * Error de una escritura en PowerFlights (alta, cambio o baja de una
 * credencial). Un rechazo (todo 4xx) dice que no se aplicó, igual que una
 * falla de las lecturas que preceden a la escritura (`writeNotSent`). Un
 * timeout, una respuesta ilegible del hub en la escritura misma, un 5xx o la
 * falta de respuesta no dicen nada: PowerFlights pudo guardar el cambio antes
 * de que se perdiera la respuesta, y eso se informa como resultado incierto.
 */
export function toAirWriteError(error: unknown): AirRequestError {
  const facts = errorFacts(error);
  const { key, fields } = messageFor(facts);
  const unconfirmed =
    !facts.writeNotSent && (key === 'airConnections.errors.unavailable' || key === 'airConnections.errors.generic');
  return unconfirmed
    ? new AirRequestError('airConnections.errors.writeUncertain', [], true)
    : new AirRequestError(key, fields);
}

/**
 * Error al guardar la conexión. client-api valida una key nueva con PowerFlights
 * ANTES de guardarla, así que un hub sin respuesta ahí sí prueba que no se
 * guardó (es una lectura del hub y una escritura local que no llegó a pasar).
 */
export function toAirConnectionSaveError(error: unknown): AirRequestError {
  const mapped = toAirRequestError(error);
  return mapped.messageKey === 'airConnections.errors.unavailable'
    ? new AirRequestError('airConnections.errors.keyNotValidated')
    : mapped;
}

/** Qué escrituras permite el diálogo de una credencial. */
export function credentialDialogControls(state: {
  saving: boolean;
  deleting: boolean;
  saveError?: AirRequestError | null;
  deleteError?: AirRequestError | null;
}) {
  const busy = state.saving || state.deleting;
  // Con un resultado incierto ninguna escritura se vuelve a mandar desde el
  // diálogo: se cierra y se revisa la lista.
  const uncertain = Boolean(state.saveError?.uncertain || state.deleteError?.uncertain);
  return { busy, uncertain, canWrite: !busy && !uncertain };
}

export const airAgencyKeys = {
  connection: (agencyId: number) => ['air-connection', agencyId] as const,
  providers: (agencyId: number) => ['air-providers', agencyId] as const,
};

/**
 * Opciones de las escrituras de credenciales: nunca se reintentan solas, y con
 * un resultado incierto se relee la lista de proveedores para que el admin vea
 * lo que quedó antes de volver a guardar.
 */
export function credentialWriteOptions(
  cache: { invalidateQueries: (key: readonly unknown[]) => unknown },
  agencyId: number,
) {
  return {
    retry: false as const,
    onSuccess: () => {
      cache.invalidateQueries(airAgencyKeys.providers(agencyId));
    },
    onError: (error: AirRequestError) => {
      if (error.uncertain) cache.invalidateQueries(airAgencyKeys.providers(agencyId));
    },
  };
}
